import { Formik, Form, FieldArray } from "formik";
import TextField from "../components/common/TextField";
import Button from "../components/common/Button";
import RadioGroup from "../components/common/RadioGroup";
import { ReactNode, useMemo } from "react";
import {
  useFetchAllHeadersQuery,
  useFetchAllModifierGroupsQuery,
} from "../store";
import classNames from "classnames";

interface ItemFormProps {
  initialValues: any;
  validationSchema: any;
  onSubmit: (
    values: any,
    formikHelpers: { resetForm: () => void }
  ) => Promise<void>;
  isLoading?: boolean;
  restaurantId: string;
}

const sectionTitleClasses = "mr-auto mt-4 text-primary font-semibold";
const vatOptions = [
  { label: "0%", value: 0 },
  { label: "10%", value: 10 },
  { label: "20%", value: 20 },
];

const ItemForm: React.FC<ItemFormProps> = ({
  initialValues,
  validationSchema,
  onSubmit,
  isLoading,
  restaurantId,
}) => {
  // Fetch available headers & modifier groups
  const { data: headers } = useFetchAllHeadersQuery(restaurantId);
  const { data: modifierGroups } = useFetchAllModifierGroupsQuery(restaurantId);

  const availableHeaders = useMemo(() => {
    if (!headers) return [];
    return headers.map((h: any) => ({ value: h._id, label: h.displayName }));
  }, [headers]);

  const availableModifierGroups = useMemo(() => {
    if (!modifierGroups) return [];
    return modifierGroups.map((mg: any) => ({
      value: mg._id,
      label: mg.displayName,
    }));
  }, [modifierGroups]);

  const errorMessageClasses = classNames(
    "w-full text-sm text-red-700 border-b border-red-700 rounded"
  );

  return (
    <Formik
      initialValues={{
        ...initialValues,
        vatRatePercentage: initialValues.vatRatePercentage || 10,
      }}
      validationSchema={validationSchema}
      onSubmit={onSubmit}
      enableReinitialize
    >
      {({
        values,
        errors,
        touched,
        handleChange,
        handleBlur,
        isSubmitting,
        setFieldValue,
      }) => (
        <Form className="mx-auto w-3/5 flex flex-col items-center mt-12 gap-3">
          {/* Basic Info */}
          <TextField
            id="displayName"
            value={values.displayName}
            onChange={handleChange}
            onBlur={handleBlur}
            errors={errors}
            touched={touched}
            placeholder="Nom du produit"
          />

          <TextField
            id="description"
            value={values.description}
            onChange={handleChange}
            onBlur={handleBlur}
            errors={errors}
            touched={touched}
            placeholder="Description"
          />

          {/* Pricing */}
          <h3 className={sectionTitleClasses}>Prix & TVA</h3>
          <TextField
            id="price"
            value={values.price}
            onChange={handleChange}
            onBlur={handleBlur}
            errors={errors}
            touched={touched}
            placeholder="Prix"
          />
          <RadioGroup
            label="Taux TVA (%)"
            options={vatOptions}
            value={values.vatRatePercentage}
            onChange={(val) => setFieldValue("vatRatePercentage", Number(val))}
          />

          {/* Display Order */}
          <h3 className={sectionTitleClasses}>Ordre d&apos;affichage</h3>

          <TextField
            id="displayOrder"
            value={values.displayOrder}
            onChange={handleChange}
            onBlur={handleBlur}
            errors={errors}
            touched={touched}
            placeholder="Ordre d'affichage"
          />

          {/* Headers Selection */}
          <h3 className={sectionTitleClasses}>Catégories</h3>
          <FieldArray name="headers">
            {({ push, remove }) => (
              <div className="w-full flex flex-col gap-2">
                {values.headers.map((h: any, index: number) => (
                  <div
                    key={h._id || index}
                    className="flex items-center justify-between border rounded p-2"
                  >
                    <span>{h.displayName}</span>
                    <Button
                      className="bg-primary text-white px-2 py-1 rounded"
                      onClick={() => remove(index)}
                    >
                      Supprimer
                    </Button>
                  </div>
                ))}
                <select
                  className="border rounded p-2"
                  onChange={(e) => {
                    const selected = availableHeaders.find(
                      (o: any) => o.value === e.target.value
                    );
                    if (selected) {
                      push({
                        _id: selected.value,
                        displayName: selected.label,
                      });
                    }
                    e.target.value = "";
                  }}
                  defaultValue=""
                >
                  <option
                    value=""
                    disabled
                  >
                    Ajouter un catégorie
                  </option>
                  {availableHeaders
                    .filter(
                      (opt: any) =>
                        !values.headers.some(
                          (sel: any) => sel._id === opt.value
                        )
                    )
                    .map((opt: any) => (
                      <option
                        key={opt.value}
                        value={opt.value}
                      >
                        {opt.label}
                      </option>
                    ))}
                </select>
              </div>
            )}
          </FieldArray>
          {errors.headers && touched.headers && (
            <div className={errorMessageClasses}>
              {errors.headers as ReactNode}
            </div>
          )}

          {/* Modifier Groups Selection */}
          <h3 className={sectionTitleClasses}>Groupes de modificateurs</h3>
          <FieldArray name="modifierGroups">
            {({ push, remove }) => (
              <div className="w-full flex flex-col gap-2">
                {values.modifierGroups.map((mg: any, index: number) => (
                  <div
                    key={mg._id || index}
                    className="flex items-center justify-between border rounded p-2"
                  >
                    <span>{mg.displayName}</span>
                    <Button
                      className="bg-primary text-white px-2 py-1 rounded"
                      onClick={() => remove(index)}
                    >
                      Supprimer
                    </Button>
                  </div>
                ))}
                <select
                  className="border rounded p-2"
                  onChange={(e) => {
                    const selected = availableModifierGroups.find(
                      (o: any) => o.value === e.target.value
                    );
                    if (selected) {
                      push({
                        _id: selected.value,
                        displayName: selected.label,
                      });
                    }
                    e.target.value = "";
                  }}
                  defaultValue=""
                >
                  <option
                    value=""
                    disabled
                  >
                    Ajouter un groupe
                  </option>
                  {availableModifierGroups
                    .filter(
                      (opt: any) =>
                        !values.modifierGroups.some(
                          (sel: any) => sel._id === opt.value
                        )
                    )
                    .map((opt: any) => (
                      <option
                        key={opt.value}
                        value={opt.value}
                      >
                        {opt.label}
                      </option>
                    ))}
                </select>
              </div>
            )}
          </FieldArray>
          {errors.modifierGroups && touched.modifierGroups && (
            <div className={errorMessageClasses}>
              {errors.modifierGroups as ReactNode}
            </div>
          )}

          <Button
            className="w-1/2 justify-center font-bold py-2 bg-primary rounded text-white mt-4"
            disabled={isSubmitting || isLoading}
            type="submit"
          >
            Enregistrer
          </Button>
        </Form>
      )}
    </Formik>
  );
};

export default ItemForm;
